const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface MercuryAccount {
  id: string;
  name: string;
  status: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
  currentBalance: number;
  availableBalance: number;
  kind: string;
  canReceiveTransactions: boolean;
  createdAt: string;
}

export interface MercuryAccountsResponse {
  accounts: MercuryAccount[];
  total: number;
}

export interface MercuryTransaction {
  id: string;
  amount: number;
  bankDescription: string | null;
  counterpartyId: string;
  counterpartyName: string;
  counterpartyNickname: string | null;
  createdAt: string;
  dashboardLink: string;
  details: string | null;
  estimatedDeliveryDate: string;
  externalMemo: string | null;
  failedAt: string | null;
  kind: string;
  note: string | null;
  postedAt: string | null;
  reasonForFailure: string | null;
  status: string;
}

export interface MercuryTransactionsResponse {
  transactions: MercuryTransaction[];
  total: number;
}

async function callMercuryFunction(body: Record<string, unknown>) {
  const apiUrl = `${SUPABASE_URL}/functions/v1/mercury-balances`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `Mercury API error: ${response.status}`);
  }

  return response.json();
}

export async function testMercuryConnection(
  mercuryApiKey: string
): Promise<MercuryAccountsResponse> {
  return callMercuryFunction({ mercuryApiKey, action: 'testConnection' });
}

export async function fetchMercuryAccounts(
  mercuryApiKey: string
): Promise<MercuryAccountsResponse> {
  return callMercuryFunction({ mercuryApiKey, action: 'fetchAccounts' });
}

export async function fetchMercuryTransactions(
  mercuryApiKey: string,
  accountId: string,
  startDate?: string,
  endDate?: string,
  limit?: number
): Promise<MercuryTransactionsResponse> {
  return callMercuryFunction({
    mercuryApiKey,
    action: 'fetchTransactions',
    accountId,
    startDate,
    endDate,
    limit: limit || 100,
  });
}
